import { reactive } from 'vue'
import axios from 'axios'
import apiClient from '@/lib/API/client'
import { isLikelyNetworkError } from '@/lib/API/network-error'
import { hasValue } from '@/lib/helpers/numeric'
import { lookupUpload, normalizeUploadLookupFilename, UPLOAD_SENSOR_TYPE_MOVEPRO } from '@/lib/helpers/upload-lookup'
import { getInteractiveTutorialPrincipalId, notifyMoveProSessionUploaded } from '@/lib/hooks/use-interactive-tutorial'
import { sendSensorFileHeartrateChoice, sensorFileMetadataFields } from '@/lib/sessions/sensor-file-metadata'
import Memory from '@/lib/memory'
import Storage from '@/lib/memory/Storage'
import { arrayBufferToBase64 } from './common'
import { appendUploadSourceDevice } from '@/lib/helpers/upload-source'

const sessionsStorage = Storage.make().dataDirectory()
const sessionsStoragePath = 'sessions'
const MAPBOX_GEOCODING_TOKEN = import.meta.env.VITE_APP_MAPBOX_TOKEN
const GEOLOCATION_REQUEST_TIMEOUT_MS = 1500
// What the rider chose for a record on its page: the horse, rider and discipline, what is sent
// with them (sensorFileMetadataFields), and the Polar capture sent once the file is.
const RECORD_CHOICES = ['horseId', 'riderId', 'discipline', 'goal', 'goal_value', 'metadataSource', 'heartrateUploadId']
// The day in a file's name (getDatFilename: D<index>_<uuid>_<YYYYMMDD>_<hhmm>.dat), on the phone's time.
const getFilenameDay = (filename) => /_(\d{8})_\d{4}\.dat$/.exec(String(filename ?? ''))?.[1]
export default async ({
    uploaded = undefined,
    city = undefined,
    filename = undefined,
    uuid = undefined,
    language = '',
    selected = true,
    syncedPercent = 0,
    date = undefined,
    weekNumber = '',
    iTOW = '',
    size = '',
    firmware = '',
    index = '',
    startPage = '',
    numberOfPages = '',
    latitude = '',
    longitude = '',
    discipline = '',
    horseId = undefined,
    riderId = undefined,
    goal = '',
    goal_value = '',
    uploadedPercent = 0,
    uploading = false,
    uploadLookup = null,
    forceUpload = false,
    metadataSource = '',
}) => {
    if (!uuid) throw new Error('File must be linked to a uuid')

    // ************************************************
    // ****************  properties ****************
    // ***********************************************
    const pagesSynced = []
    const properties = reactive({
        uploaded,
        city,
        cityProvider: null,
        filename,
        uuid,
        language,
        selected,
        syncedPercent,
        date,
        weekNumber,
        iTOW,
        size,
        firmware,
        index,
        startPage,
        numberOfPages,
        latitude,
        longitude,
        discipline,
        horseId,
        riderId,
        goal,
        goal_value,
        uploadedPercent,
        uploading,
        uploadLookup,
        forceUpload,
        metadataSource,
    })

    const serializeProperties = () => {
        const serializedProperties = { ...properties }
        delete serializedProperties.uploading
        delete serializedProperties.cityProvider
        const normalizedCity = typeof properties.city === 'string' ? properties.city.trim() : ''
        const looksLikeCoordinates = /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(normalizedCity)
        if (['mapbox', 'coordinates'].includes(properties.cityProvider) || looksLikeCoordinates) {
            delete serializedProperties.city
        }
        return serializedProperties
    }

    const buildUploadPayload = () => {
        const size = pagesSynced.reduce((accumulator, page) => accumulator + page.length, 0)
        if (!size) {
            return null
        }

        const buf = new ArrayBuffer(size)
        const data = new Uint8Array(buf)
        let j = 0
        for (const key in pagesSynced) {
            const pageSize = pagesSynced[key].length
            for (let i = 0; i < pageSize; i++) {
                data[j] = pagesSynced[key][i]
                j++
            }
        }

        return data
    }

    const persistUploadPayload = async (data = null) => {
        const payload = data || buildUploadPayload()
        if (!(payload instanceof Uint8Array) || payload.length === 0 || !properties.filename) {
            return false
        }

        await sessionsStorage.writeFile(sessionsStoragePath, properties.filename, arrayBufferToBase64(payload.buffer), {
            recursive: true,
            encoding: undefined,
        })

        return true
    }

    const readPersistedUploadPayload = async () => {
        if (!properties.filename) {
            return null
        }

        try {
            const payload = await sessionsStorage.readAsArrayBufferView(sessionsStoragePath, properties.filename)
            return payload?.length ? payload : null
        } catch {
            return null
        }
    }

    const removePersistedUploadPayload = async () => {
        if (!properties.filename) {
            return
        }

        try {
            const fullPath = sessionsStorage.getFilePath(sessionsStoragePath, properties.filename)
            await sessionsStorage.deleteFile(fullPath)
        } catch {}
    }

    const persistProperties = async () => {
        await Memory.set(getFilename(), serializeProperties())
    }

    const getUploadLookupFilename = () => normalizeUploadLookupFilename(properties.filename)

    const getUploadLookupSensorType = () => UPLOAD_SENSOR_TYPE_MOVEPRO

    const normalizeLookupText = (value) => (typeof value === 'string' ? value : '')

    const normalizeLookupDiscipline = (value) => (!hasValue(value) || value === 'default' ? '' : String(value))

    const normalizeUploadLookupState = (lookup = {}) => ({
        filename: typeof lookup?.filename === 'string' ? lookup.filename : getUploadLookupFilename(),
        sensor_type:
            typeof lookup?.sensor_type === 'string' && lookup.sensor_type
                ? lookup.sensor_type
                : getUploadLookupSensorType(),
        exists: typeof lookup?.exists === 'boolean' ? lookup.exists : null,
        count: Number(lookup?.count) || 0,
        uploads: Array.isArray(lookup?.uploads)
            ? lookup.uploads.map((upload) => ({
                  upload_id: Number(upload?.upload_id) || null,
                  session_id: Number(upload?.session_id) || null,
                  session_name: normalizeLookupText(upload?.session_name),
                  discipline: normalizeLookupDiscipline(upload?.discipline),
              }))
            : [],
        error: typeof lookup?.error === 'string' && lookup.error.trim() ? lookup.error : null,
        status: typeof lookup?.status === 'string' ? lookup.status : 'success',
        stale: lookup?.stale === true,
        checkedAt: Number(lookup?.checkedAt) || null,
        lastFailureAt: Number(lookup?.lastFailureAt) || null,
    })

    const applyUploadLookupState = async (lookup, { persist = true } = {}) => {
        properties.uploadLookup = normalizeUploadLookupState({
            ...lookup,
            checkedAt: Number(lookup?.checkedAt) || Date.now(),
            stale: false,
            lastFailureAt: null,
        })

        if (properties.uploadLookup.status === 'success' && properties.uploadLookup.error === null) {
            if (properties.uploadLookup.exists === true) {
                properties.uploaded = properties.forceUpload !== true
                if (properties.forceUpload !== true) {
                    properties.selected = false
                }
            } else if (properties.uploadLookup.exists === false) {
                properties.uploaded = false
                properties.forceUpload = false
            }
        }

        if (persist) {
            await persistProperties()
        }

        return properties.uploadLookup
    }

    const markUploadLookupUnknown = async ({ persist = true } = {}) => {
        const previousLookup =
            properties.uploadLookup && properties.uploadLookup.status === 'success'
                ? normalizeUploadLookupState(properties.uploadLookup)
                : null

        properties.uploadLookup = previousLookup
            ? {
                  ...previousLookup,
                  status: 'unknown',
                  stale: true,
                  lastFailureAt: Date.now(),
              }
            : {
                  filename: getUploadLookupFilename(),
                  sensor_type: getUploadLookupSensorType(),
                  exists: null,
                  count: 0,
                  uploads: [],
                  error: null,
                  status: 'unknown',
                  stale: false,
                  checkedAt: null,
                  lastFailureAt: Date.now(),
              }

        if (persist) {
            await persistProperties()
        }

        return properties.uploadLookup
    }

    const setForceUpload = async (nextForceUpload = true) => {
        properties.forceUpload = nextForceUpload === true

        if (properties.forceUpload) {
            properties.uploaded = false
            properties.selected = true
            properties.uploadedPercent = 0
        } else if (properties.uploadLookup?.status === 'success' && properties.uploadLookup?.exists === true) {
            properties.uploaded = true
            properties.selected = false
        }

        await persistProperties()
    }

    const markAsUploaded = async ({ uploadLookup = null } = {}) => {
        properties.uploaded = true
        properties.forceUpload = false
        properties.selected = false
        properties.uploadedPercent = 100
        properties.uploading = false

        if (uploadLookup) {
            properties.uploadLookup = normalizeUploadLookupState({
                ...uploadLookup,
                exists: true,
                status: 'success',
                stale: false,
                checkedAt: Number(uploadLookup?.checkedAt) || Date.now(),
            })
        }

        await persistProperties()
        await removePersistedUploadPayload()
    }

    const markAsUploadFailed = async ({ uploadedPercent = 0 } = {}) => {
        properties.uploaded = false
        properties.uploadedPercent = uploadedPercent
        properties.uploading = false
        await persistProperties()
    }

    const isAlreadyUploadedError = (error) =>
        Number(error?.response?.status) === 409 &&
        /already uploaded/i.test(String(error?.response?.data?.message || error?.message || ''))

    // ************************************************
    // ****************  methods ****************
    // ***********************************************
    const getDateString = (type) => {
        let date = getDate()
        let YYYY = date.getFullYear().toString()
        let MM = (date.getMonth() + 1).toString()
        if (MM.length == 1) MM = '0' + MM
        let DD = date.getDate().toString()
        if (DD.length == 1) DD = '0' + DD
        let hh = date.getHours().toString()
        if (hh.length == 1) hh = '0' + hh
        let mm = date.getMinutes().toString()
        if (mm.length == 1) mm = '0' + mm
        let ss = date.getSeconds().toString()
        if (ss.length == 1) ss = '0' + ss
        if (type == 1) return YYYY + '/' + MM + '/' + DD + ' - ' + hh + ':' + mm + ':' + ss
        else if (type == 2) return YYYY + MM + DD + '_' + hh + mm // used for the filename https://github.com/Alogo-Analysis/mobile-app/issues/208#issuecomment-851439094
    }
    const getSizeString = () => {
        const size = properties.size
        let sizeStr = size.toString() + ' Bytes'
        if (size == 1) sizeStr = size + ' Byte'
        else if (size <= 1000) sizeStr = size + ' Bytes'
        else if (size > 1000 * 1000) sizeStr = Math.round(size / 1000.0 / 1000.0) + ' MB'
        else if (size > 1000) sizeStr = Math.round(size / 1000.0) + ' KB'
        return sizeStr
    }

    const getFilename = (extension = null) => {
        return 'D' + properties.index + '_' + properties.uuid.substring(0, 8) + (extension ? `_${extension}` : '')
    }

    const getDatFilename = () => {
        return getFilename() + '_' + getDateString(2) + '.dat'
    }

    const getCoordinatesLabel = () => {
        const latitude = Number(properties.latitude)
        const longitude = Number(properties.longitude)
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            return `${latitude.toPrecision(6)}, ${longitude.toPrecision(6)}`
        }

        return [properties.latitude, properties.longitude].filter((value) => value !== '' && value !== null).join(', ')
    }

    const getGeocodingLanguage = () => {
        const normalizedLanguage = typeof properties.language === 'string' ? properties.language.trim() : ''
        return normalizedLanguage || 'en'
    }

    const isCoordinateLikeCity = (value) =>
        typeof value === 'string' && /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(value.trim())

    const getBigDataCloudCity = async () => {
        const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${properties.latitude}&longitude=${properties.longitude}&localityLanguage=${getGeocodingLanguage()}`
        let response = {}
        try {
            response = await axios.get(url, {
                timeout: GEOLOCATION_REQUEST_TIMEOUT_MS,
            })
        } catch (error) {}

        const locationLabel = [response?.data?.city, response?.data?.locality, response?.data?.principalSubdivision]
            .map((value) => (typeof value === 'string' ? value.trim() : ''))
            .find(Boolean)

        return {
            value: locationLabel || '',
            resolved: Boolean(locationLabel),
            provider: 'bigdatacloud',
        }
    }

    const getMapboxCity = async () => {
        if (!MAPBOX_GEOCODING_TOKEN) {
            return {
                value: '',
                resolved: false,
                provider: 'mapbox',
            }
        }

        const params = new URLSearchParams({
            longitude: String(properties.longitude),
            latitude: String(properties.latitude),
            language: getGeocodingLanguage(),
            access_token: MAPBOX_GEOCODING_TOKEN,
        })
        let response = {}
        try {
            response = await axios.get(`https://api.mapbox.com/search/geocode/v6/reverse?${params.toString()}`, {
                timeout: GEOLOCATION_REQUEST_TIMEOUT_MS,
            })
        } catch (error) {}

        const features = Array.isArray(response?.data?.features) ? response.data.features : []
        const directFeature = features.find((feature) =>
            ['place', 'locality', 'district', 'region'].includes(feature?.properties?.feature_type)
        )
        const context = features[0]?.properties?.context || {}
        const locationLabel = [
            directFeature?.properties?.name_preferred,
            directFeature?.properties?.name,
            context.place?.name,
            context.locality?.name,
            context.district?.name,
            context.region?.name,
        ]
            .map((value) => (typeof value === 'string' ? value.trim() : ''))
            .find(Boolean)

        return {
            value: locationLabel || '',
            resolved: Boolean(locationLabel),
            provider: 'mapbox',
        }
    }

    const getCity = async () => {
        if (!properties.latitude || !properties.longitude) {
            return {
                value: '',
                resolved: false,
                provider: null,
            }
        }

        const bigDataCloudCity = await getBigDataCloudCity()
        if (bigDataCloudCity.resolved) {
            return bigDataCloudCity
        }

        const mapboxCity = await getMapboxCity()
        if (mapboxCity.resolved) {
            return mapboxCity
        }

        return {
            value: getCoordinatesLabel(),
            resolved: false,
            provider: 'coordinates',
        }
    }

    const refreshCity = async ({ force = false } = {}) => {
        const currentCity = typeof properties.city === 'string' ? properties.city.trim() : ''
        const shouldRefresh =
            force ||
            !currentCity ||
            isCoordinateLikeCity(currentCity) ||
            properties.cityProvider === 'coordinates' ||
            properties.cityProvider === null

        if (!shouldRefresh) {
            return properties.city || ''
        }

        const cityLookup = await getCity()
        properties.city = cityLookup.value
        properties.cityProvider = cityLookup.provider

        if (cityLookup.resolved && cityLookup.provider === 'bigdatacloud') {
            await persistProperties()
        }

        return properties.city || ''
    }

    const refreshUploadLookup = async ({ timeout = 5000 } = {}) => {
        const lookup = await lookupUpload(
            {
                filename: getUploadLookupFilename(),
                sensor_type: getUploadLookupSensorType(),
            },
            { timeout }
        )

        if (!lookup) {
            return null
        }

        await applyUploadLookupState(lookup)
        return properties.uploadLookup
    }

    const isUploaded = async () => {
        try {
            const lookup = await refreshUploadLookup({ timeout: 5000 })
            if (lookup?.exists === true) {
                return properties.forceUpload !== true
            }
            if (lookup) return lookup?.exists
        } catch (error) {
            if (isLikelyNetworkError(error)) {
                await markUploadLookupUnknown()
            }
        }

        if (properties.uploadLookup?.exists === true) {
            return properties.forceUpload !== true
        }

        return typeof properties.uploadLookup?.exists === 'boolean' ? properties.uploadLookup.exists : null
    }

    const upload = async ({ sessionId = null, manageUploadingState = true } = {}) => {
        const uploadingUserId = getInteractiveTutorialPrincipalId()

        if (manageUploadingState) {
            properties.uploading = true
        }

        try {
            //upload the file to the server
            const formData = new FormData()
            let data = buildUploadPayload()
            if (data?.length > 0) {
                await persistUploadPayload(data)
            } else {
                data = await readPersistedUploadPayload()
            }

            if (!(data instanceof Uint8Array) || data.length === 0) {
                const error = new Error('sensor-file-upload-source-missing')
                error.code = 'sensor-file-upload-source-missing'
                throw error
            }

            formData.append(
                'source',
                new Blob([data], {
                    type: 'application/octet-stream',
                }),
                properties.filename
            )
            formData.append('size', properties.size)
            formData.append('isodate', getISODateString())
            formData.append('latitude', properties.latitude)
            formData.append('longitude', properties.longitude)
            formData.append('firmware_version', properties.firmware)
            formData.append('serial_id', properties.uuid || 'unknown')
            for (const [field, value] of Object.entries(sensorFileMetadataFields(properties))) {
                formData.append(field, value)
            }
            const normalizedSessionId = Number(sessionId)
            if (Number.isFinite(normalizedSessionId) && normalizedSessionId > 0) {
                formData.append('session_id', normalizedSessionId)
            }

            // ! not possible right now because the upload will be very large
            // if (properties?.ecgStream?.length > 0) {
            //     formData.append('ecgStream', JSON.stringify(properties.ecgStream))
            // }
            // we only use the light format so this should not change
            formData.append('light', 1)
            await appendUploadSourceDevice(formData)

            try {
                const response = await apiClient.post('/uploads', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        properties.uploadedPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                    },
                })
                const responseData = response?.data || null
                const uploadedSessionId = Number(responseData?.session_id)
                const uploadedSessionName =
                    typeof responseData?.session_name === 'string' ? responseData.session_name : ''
                const uploadedUploadId = Number(responseData?.upload_id) || null

                // Only for a file the server takes now: one it already had (409) takes none of its choices.
                await sendSensorFileHeartrateChoice(properties, uploadedSessionId)

                notifyMoveProSessionUploaded(
                    Number.isFinite(uploadedSessionId) && uploadedSessionId > 0 ? uploadedSessionId : null,
                    uploadingUserId
                )

                await markAsUploaded({
                    uploadLookup: {
                        filename: getUploadLookupFilename(),
                        sensor_type: getUploadLookupSensorType(),
                        exists: true,
                        count: 1,
                        uploads:
                            Number.isFinite(uploadedSessionId) && uploadedSessionId > 0
                                ? [
                                      {
                                          upload_id: uploadedUploadId,
                                          session_id: uploadedSessionId,
                                          session_name: uploadedSessionName,
                                          discipline: responseData?.discipline,
                                      },
                                  ]
                                : [],
                    },
                })

                return responseData
            } catch (error) {
                if (isAlreadyUploadedError(error)) {
                    await markAsUploaded()

                    try {
                        await refreshUploadLookup({ timeout: 5000 })
                    } catch {}

                    return error?.response?.data || { alreadyUploaded: true }
                }

                throw error
            }
        } catch (error) {
            if (manageUploadingState) {
                properties.uploading = false
            }
            throw error
        }
    }

    const getEstimatedDuration = () => {
        return Math.round(properties.size / 3791)
    }
    const getISODateString = () => {
        return getDate().toISOString()
    }
    const getLocalDateString = () => {
        return getDate().toLocaleDateString(properties.language)
    }
    const getDate = () => {
        if (properties.date === undefined) {
            properties.date = new Date(
                properties.weekNumber * 7 * 24 * 3600 * 1000 +
                    properties.iTOW +
                    (new Date(1980, 0, 6, 0, 0, 0, 0).getTime() - new Date(0).getTime()) -
                    new Date(0).getTimezoneOffset() * 60000
            )
        } else if (typeof properties.date === 'string' || typeof properties.date === 'number') {
            properties.date = new Date(properties.date)
        }
        return properties.date
    }
    const getWeek = (date) => {
        const onejan = new Date(date.getFullYear(), 0, 1)
        return Math.ceil(((date - onejan) / 86400000 + onejan.getDay() + 1) / 7)
    }

    const save = async (newProperties = {}) => {
        Object.assign(properties, newProperties) // keep reactivity
        // console.log('going to save', properties.filename, { ...properties })
        await Memory.set(getFilename(), serializeProperties())
    }

    // ************************************************
    // **************** instanciate properties ****************
    // ***********************************************
    if (filename === undefined) {
        properties.filename = await getDatFilename()
    }
    //should be done after the getFilename method is called
    const newProperties = await Memory.get(getFilename())
    // console.log('newProperties', newProperties, properties.filename)
    if (newProperties) {
        delete newProperties['ecgStream']
        // The sensor numbers its records from 1 again after an erase (by a Sync Box or another phone):
        // the copy kept under this key can be an older ride. The choices made for it only come with
        // the same record: the same number, the same sensor, the same day as in its name.
        const day = getFilenameDay(properties.filename)
        const isSameRecord =
            String(newProperties.index) === String(properties.index) &&
            newProperties.uuid === properties.uuid &&
            day !== undefined &&
            getFilenameDay(newProperties.filename) === day
        if (!isSameRecord) {
            for (const field of RECORD_CHOICES) delete newProperties[field]
        }
        Object.assign(properties, newProperties) // keep reactivity
    }
    const needsCityLookup =
        city === undefined &&
        (properties.city === undefined ||
            properties.city === null ||
            String(properties.city).trim() === '' ||
            isCoordinateLikeCity(properties.city))

    if (needsCityLookup) {
        await refreshCity()
    }

    if (properties.uploadLookup) {
        properties.uploadLookup = normalizeUploadLookupState(properties.uploadLookup)
    }

    if (properties.uploadLookup?.status === 'success' && properties.uploadLookup?.exists === true) {
        properties.uploaded = properties.forceUpload !== true
    } else if (properties.uploadLookup?.status === 'success' && properties.uploadLookup?.exists === false) {
        properties.uploaded = false
        properties.forceUpload = false
    }

    if (properties.uploaded === true) {
        properties.selected = false
    }

    return {
        //properties
        pagesSynced,
        properties,
        //methods
        getDateString,
        getSizeString,
        getFilename,
        getDatFilename,
        getCity,
        refreshCity,
        getUploadLookupFilename,
        getUploadLookupSensorType,
        applyUploadLookupState,
        markUploadLookupUnknown,
        refreshUploadLookup,
        isUploaded,
        upload,
        persistUploadPayload,
        readPersistedUploadPayload,
        markAsUploaded,
        markAsUploadFailed,
        setForceUpload,
        getEstimatedDuration,
        getISODateString,
        getLocalDateString,
        getDate,
        getWeek,
        save,
    }
}
